import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Settings, LogOut, Menu } from 'lucide-react'
import { useAuthContext } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'

interface TopNavProps {
  onMobileMenuToggle: () => void
}

export function TopNav({ onMobileMenuToggle }: TopNavProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, signOut } = useAuthContext()

  const userInitials = user?.email?.slice(0, 2).toUpperCase() || 'U'
  const userEmail = user?.email || t('topNav.guest')

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) {
      toast.error(t('topNav.logoutError'))
      return
    }
    toast.success(t('topNav.logoutSuccess'))
    navigate('/login')
  }

  return (
    <header className="flex h-16 items-center justify-between border-b bg-background px-4 sm:px-6 shadow-sm z-10">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="sm"
        className="lg:hidden hover:bg-sidebar-accent"
        onClick={onMobileMenuToggle}
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="flex items-center ml-auto">
        {/* Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 rounded-full p-0">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel>
              <div className="space-y-1">
                <p className="font-medium truncate">{userEmail}</p>
                <p className="text-xs text-muted-foreground">{t('topNav.account')}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings className="mr-2 h-4 w-4" />
                {t('topNav.accountSettings')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('topNav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
